// Ce que chaque moteur garde pour lui sur un GPU, tel qu'il le règle par défaut, et l'arrondi qu'il impose aux
// poids de certains MoE. Deux sources, jamais une constante devinée :
//  - le code des moteurs : la part de mémoire automatique de SGLang (memory_hook.py), la marge de « --fit »
//    de llama.cpp (1 024 Mio par GPU, activé par défaut) ;
//  - leurs journaux réels : 108 démarrages de serveur publiés par InferenceX (octobre 2026), où vLLM et SGLang
//    écrivent les poids chargés, le cache alloué et, depuis vLLM 0.21, la mémoire de leurs graphes CUDA.
// compute() les applique quand le moteur et le GPU visés sont connus (Inputs.target) ; une valeur saisie par
// le visiteur les remplace.

import { isConsole, type Gpu } from "./hardware.ts";
import type { ModelPreset } from "./models.ts";

/** Ce qu'il faut savoir du GPU visé. */
export type GpuSpec = Pick<Gpu, "id" | "group" | "vram" | "memGiB" | "link">;

/** Moteur et GPU visés par un calcul, et les réglages que le visiteur a saisis lui-même. */
export type Target = {
  engine: string;
  gpu: GpuSpec;
  /** Le GPU affiche aussi l'écran (bureau, navigateur) : 1 Gio de plus réservé, cartes de particulier seulement. */
  display?: boolean;
  userUtilization?: boolean;
  userOverhead?: boolean;
};

/** Familles des constantes mesurées : NVIDIA Blackwell de serveur (B200, B300), AMD Instinct, et le reste
 *  (constantes mesurées sur Hopper, appliquées aux autres NVIDIA et aux cartes non mesurées). */
export type GpuFamily = "blackwell" | "instinct" | "other";
export const gpuFamily = (g: Pick<Gpu, "id">): GpuFamily =>
  g.id === "b200" || g.id === "b300" ? "blackwell" : /^mi\d/.test(g.id) ? "instinct" : "other";

const isServer = (g: Pick<Gpu, "group">) => g.group === "NVIDIA datacenter" || g.group === "AMD & Intel";
const isUnifiedMemory = (g: Pick<Gpu, "group">) => g.group === "Unified memory";
/** Mémoire totale du GPU en Mio, comme la lit le moteur (torch : total_memory). */
const memMiB = (g: GpuSpec) => (g.memGiB ?? g.vram) * 1024;

/**
 * mem_fraction_static de SGLang quand on ne le fixe pas (python/sglang/srt/arg_groups/memory_hook.py, octobre
 * 2026) : réserve = 512 Mo + 1,5 Mo par token de prefill découpé (2 048 au moins) + 1 Go × TP × PP / 8 + 2 Mo
 * par requête des graphes CUDA de décodage, et au moins 10 Go au-delà de 60 Go de mémoire. La taille du prefill
 * et des graphes suit la mémoire du GPU. H100 : 0,835 ; B200 en TP8 : 0,852 ; RTX 4090 : 0,72.
 */
export function sglangMemFraction(miB: number, tp: number, pp: number): number {
  const prefill = miB < 20 * 1024 ? 2048 : miB < 60 * 1024 ? 4096 : miB < 160 * 1024 ? 8192 : 16384;
  const graphBs = miB < 20 * 1024 ? 8 : miB < 35 * 1024 ? (tp < 4 ? 48 : 160) : miB < 60 * 1024 ? (tp < 4 ? 32 : 160)
    : miB < 160 * 1024 ? (tp < 4 ? 256 : 512) : 512;
  let reserved = 512 + Math.max(prefill, 2048) * 1.5 + ((tp * pp) / 8) * 1024 + graphBs * 2;
  if (miB > 60 * 1024) reserved = Math.max(reserved, 10 * 1024);
  return Math.round(((miB - reserved) / miB) * 1000) / 1000;
}

/**
 * Réserve de vLLM par GPU, hors poids et cache, sous gpu_memory_utilization (0,90 par défaut) : contexte CUDA,
 * NCCL, pic d'activations et graphes CUDA. Médianes des journaux InferenceX, modèle par modèle (gpt-oss,
 * MiniMax M2.5, Kimi K2.5), octobre 2026 : 4,5 Gio sur NVIDIA de serveur (H100 4,6, H200 2,8 à 3,5, MiniMax 5,7
 * à 6,5) ; 6 Gio sur B200 et B300 (graphes CUDA de 1,7 à 3,7 Gio) ; 7 Gio sur AMD Instinct. Cartes de
 * particulier et de station de travail : non mesurées, 3 Gio comme avant (vLLM y prend des lots de prefill plus
 * petits).
 */
const VLLM_RESERVE_GIB: Record<GpuFamily | "server", number> = { blackwell: 6, instinct: 7, server: 4.5, other: 3 };

/**
 * SGLang : ce qui est déjà pris avant les poids et que mem_fraction_static compte (contexte CUDA, NCCL),
 * total − mémoire libre au chargement dans ses journaux : 2 à 2,4 Gio sur NVIDIA (H100, H200, B200, B300),
 * 6 à 9 Gio sur AMD Instinct (contexte ROCm).
 */
const SGLANG_INIT_GIB = { instinct: 7, other: 2.2 };

/**
 * PS5LM Plus, seul moyen de faire tourner llama.cpp sur une PS5 (ps5/app/model_plan.cpp, octobre 2026) : son
 * écran et l'appli prennent 0,43 Gio du tas de 11,44 (11,01 Gio libres après un redémarrage), puis son planificateur
 * garde 0,75 Gio (tampons de calcul en lots de 64 tokens, part du CPU) et une copie de l'état récurrent (le point
 * de reprise du prompt). Pas de marge « --fit » : il lance llama.cpp avec -fit off. Contrôle sur son tableau de bord
 * (Qwen3.8 27B UD-IQ2_XXS, 64K, cache q8_0) : 10,5 Gio pris sur 11,4.
 */
const CONSOLE_APP_GIB = 0.43, CONSOLE_PLANNER_GIB = 0.75;

/** llama.cpp : tampon de calcul d'un lot de 512 tokens (logits du lot en FP32, le plus gros, plus le reste). */
const llamaComputeBufferGiB = (m: Pick<ModelPreset, "vocab">) => (m.vocab * 512 * 4 + 256 * 1024 ** 2) / 1024 ** 3;

/** Part utilisable de la mémoire et surcoût par GPU (Gio), tels que ce moteur les règle par défaut sur ce GPU. */
export function engineReserve(engine: string, gpu: GpuSpec, tp: number, pp: number, model: Pick<ModelPreset, "vocab" | "parts" | "stateBytesPerSeq">): { utilization: number; overheadGiB: number } {
  const fam = gpuFamily(gpu);
  switch (engine) {
    case "vllm":
      return { utilization: 0.9, overheadGiB: fam !== "other" ? VLLM_RESERVE_GIB[fam] : isServer(gpu) ? VLLM_RESERVE_GIB.server : VLLM_RESERVE_GIB.other };
    case "sglang": {
      // Modèle avec vision : SGLang retire au moins 5 % pour l'encodeur d'images (adjust_mem_fraction_for_vlm).
      const vlm = (model.parts?.vision ?? 0) > 0 ? 0.95 : 1;
      return { utilization: sglangMemFraction(memMiB(gpu), tp, pp) * vlm, overheadGiB: fam === "instinct" ? SGLANG_INIT_GIB.instinct : SGLANG_INIT_GIB.other };
    }
    case "llamacpp":
      if (isConsole(gpu)) return { utilization: 1, overheadGiB: CONSOLE_APP_GIB + CONSOLE_PLANNER_GIB + (model.stateBytesPerSeq ?? 0) / 1024 ** 3 };
      // Toute la mémoire libre ; « --fit » (activé par défaut) en laisse 1 024 Mio par GPU, plus son tampon de
      // calcul et le contexte CUDA ou ROCm (Metal : la part GPU des Mac l'exclut déjà).
      return { utilization: 1, overheadGiB: 1 + llamaComputeBufferGiB(model) + (isUnifiedMemory(gpu) ? 0 : 0.4) };
    case "trtllm":
      return { utilization: 0.9, overheadGiB: 3 };
    default:
      return { utilization: 0.95, overheadGiB: 2 };
  }
}

/** Ce qu'un calcul retient : la règle du moteur visé, sauf ce que le visiteur a saisi, plus l'écran affiché. */
export function reserveFor(i: { target?: Target; utilization: number; overheadGiB: number; model: Pick<ModelPreset, "vocab" | "parts" | "stateBytesPerSeq"> }, tp: number, pp: number) {
  const t = i.target;
  if (!t) return { utilization: i.utilization, overheadGiB: i.overheadGiB };
  const r = engineReserve(t.engine, t.gpu, tp, pp, i.model);
  // Console : son écran est déjà dans la réserve de l'appli.
  const display = t.display && !isServer(t.gpu) && !isUnifiedMemory(t.gpu) && !isConsole(t.gpu) ? DISPLAY_GIB : 0;
  return {
    utilization: t.userUtilization ? i.utilization : r.utilization,
    overheadGiB: (t.userOverhead ? i.overheadGiB : r.overheadGiB) + display,
  };
}
/** Bureau et navigateur sur la carte qui affiche l'écran : 0,5 à 1,5 Go selon le système, 1 Gio retenu. */
export const DISPLAY_GIB = 1;

/**
 * vLLM, MoE aux experts FP4 natifs (gpt-oss en MXFP4, DeepSeek V4) : son noyau arrondit chaque dimension d'un
 * expert, sur chaque GPU, au multiple supérieur. Blackwell et AMD Instinct (FLASHINFER_TRTLLM, aiter) : 256 pour
 * la largeur et pour la dimension interne coupée par le TP (sauf DeepSeek V4 sur Instinct, voir plus bas) ; autres
 * GPU (Marlin) : 256 et 128. Facteur sur les
 * octets des experts. Calé sur InferenceX à 1 % près : gpt-oss-120b en TP8 sur B200, 360 → 512 et 2 880 → 3 072,
 * 11,39 Gio par GPU chargés ; DeepSeek V4 Pro en TP8 sur B300, 384 → 512, 134,29 Gio.
 */
export function vllmExpertPadding(model: Pick<ModelPreset, "routing" | "native" | "hidden">, gpu: Pick<Gpu, "id">, tp: number): number {
  const ffn = model.routing?.ffn;
  if (!ffn || !model.native || !/FP4/.test(model.native.label)) return 1;
  const fam = gpuFamily(gpu);
  // AMD Instinct, DeepSeek V4 (« FP4 experts + FP8 ») : vLLM y prend le MoE d'AITER propre à V4, à la largeur
  // native des experts (InferenceX 441260, MI355X en TP8, vLLM 0.28 nightly, 9 septembre 2026 : « native
  // intermediate width 384 », 104,97 Gio chargés par GPU, autant que SGLang). gpt-oss (MXFP4) reste arrondi.
  if (fam === "instinct" && /^FP4 experts/.test(model.native.label)) return 1;
  const up = (x: number, step: number) => Math.ceil(x / step) * step;
  const inner = ffn / tp;
  return (up(inner, fam === "other" ? 128 : 256) / inner) * (up(model.hidden, 256) / model.hidden);
}
